import { Button, Dialog, Image } from "@/components/Elements";
import { InputField, SelectField } from "@/components/Form";
import { useCustomForm } from "@/hooks";
import useToggle from "@/hooks/useToggle";
import { PropsWithChildren, useEffect } from "react";
import { searchPinCodeSchema, SearchPinCodeSchemaModel } from "../types/schema";
import { LatLngModel } from "@/types/common";
import { getCountries, useLocation, verifyPostcode } from "@/hooks/useLocation";
import { UserMap } from "@/components/Map";

const countryList = getCountries();

function SearchPinCode({
  location: initLocation,
  radius,
  showAddress,
  children,
  onChange,
}: PropsWithChildren<{
  location?: LatLngModel;
  radius?: number;
  showAddress?: boolean;
  onChange: (name: any, location: any) => void;
}>) {
  const { show: showModal, toggle: toggleModal } = useToggle();
  const {
    register,
    setError,
    setValue,
    handleSubmit,
    reset,
    formState,
    watch,
  } = useCustomForm<SearchPinCodeSchemaModel>({
    schema: searchPinCodeSchema,
    options: { defaultValues: { countryCode: "" } },
  });
  const { postCode, location, countryCode, address } = watch();
  const { getAddressDetail, getLocationFromAddress } = useLocation();

  useEffect(() => {
    if (initLocation) {
      setValue("location", initLocation);
      (async () => {
        const { postCode, countryCode, country, address } =
          await getAddressDetail(initLocation);
        reset((prev) => ({ ...prev, postCode, countryCode, country, address }));
      })();
    }
  }, [initLocation]);

  useEffect(() => {
    if (postCode || countryCode) {
      updateLocation();
    }
  }, [postCode, countryCode]);

  const updateLocation = async () => {
    let location,
      address = {
        postCode,
        countryCode,
      };
    if (verifyPostcode(address)) {
      location = await getLocationFromAddress(address);
    }
    setValue("location", location);
    if (!location?.lat || !location?.lng) {
      setError("postCode", { message: "Invalid Post Code" });
    } else {
      setError("postCode", {});
    }
  };

  const onCountryChange = () => {
    setValue("postCode", "");
  };

  async function handleSearch(model: SearchPinCodeSchemaModel) {
    const { location } = model;
    if (!location?.lat || !location?.lng) {
      setError("postCode", { message: "Invalid Post Code" });
      return "";
    } else {
      onChange("location", location);
      toggleModal();
    }
  }

  watch("location");

  return (
    <>
      <div onClick={toggleModal}>
        <div className="flex">{children}</div>
        {showAddress && <div className="text-[12px]">{address}</div>}
      </div>
      {showModal && (
        <Dialog isOpen={showModal} onClose={toggleModal} hideHeader>
          <div className="flex flex-col items-center">
            <div className="w-[80px] h-[80px] items-center justify-center">
              <Image src="search-icon.png" className="w-full h-full" />
            </div>
            <div className="text-[18px]">Search using post code</div>
          </div>
          <div className="flex-1 mt-[5px]">
            <SelectField
              registration={{
                ...register("countryCode"),
                onChange: async (e: any) => onCountryChange(),
              }}
              className="text-[20px]"
              label="Country"
              options={countryList}
            />
            <InputField
              type="text"
              placeholder="Enter your post code here"
              label="Post Code"
              registration={register("postCode")}
              error={formState.errors["postCode"]}
            />
            <UserMap
              location={location}
              radius={radius}
              onChange={(name: string, value: LatLngModel) =>
                setValue("location", value)
              }
            />
            <div className="flex justify-between mt-[20px]">
              <Button onClick={toggleModal} className="w-[45%] bg-[#808080]">
                Cancel
              </Button>
              <Button onClick={handleSubmit(handleSearch)} className="w-[45%]">
                Select
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}

export default SearchPinCode;
